import axios from "@/axios";
import umbracoFormsAxios from "@/axios/umbracoForms";
import { IContentOverviewApiGetByFilterResult } from "@/models/IContentOverviewApiGetByFilterResult";
import { ProductTile } from "@/models/ProductList";
import { Cart } from "@/models/Cart";
import { Login, ChangePassword } from "@/models/Member";
import { Problem } from "@/models/Error";
import {
  Checkout,
  FreightModel,
  PlaceOrderCommand,
  SetDeliveryCommand,
  UpdateCustomerCommand,
} from "@/models/Checkout";
import {
  ProductSearchResponse,
  ProductsSearchRequest,
  SearchCasesResponse,
  SearchRequest,
  SearchResponse,
  Sorting,
} from "@/models/Search";
import {
  CustomerNumberValidatorResult,
  EmailValidatorResult,
  RegisterAccountBusiness,
  RegisterAccountPrivate,
} from "@/models/AccountRegister";
import { emitter } from "@/services/Emitter";
import { IServicePoint } from "@/models/IServicePoint";
import { ISalesman } from "@/models/ISalesman";
import { IAntiForgeryToken } from "@/models/umbraco-forms/IAntiForgeryToken";
import { IUmbracoForm } from "@/models/umbraco-forms/IUmbracoForm";
import { IOrderHistoryHeader, OrderHistoryType } from "@/models/IOrderHistoryHeader";
import { IOrderHistoryLine } from "@/models/IOrderHistoryLine";
import { QuickSearchViewModel } from "@/models/QuickSearchViewModel";
import { useCart } from "@/store/useCart";
import { SearchCasesRequest } from "@/models/Cases";

const addToDataLayer = (data) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push(data);
};

const login = async (username: string, password: string): Promise<Login> => {
  try {
    return await axios.post("account/signin", { username, password });
  } catch (e) {
    return Promise.reject(e);
  }
};

const changePassword = async (
  email: string,
  currentPassword: string,
  password: string,
  confirmPassword: string
): Promise<string> => {
  try {
    return await axios.post("account/changepassword", {
      email,
      currentPassword,
      newPassword: password,
      newPassword2: confirmPassword,
    });
  } catch (e) {
    return Promise.reject(e);
  }
};

export const ProductApi = {
  productRecommendations: async (productId: string): Promise<ProductTile[]> => {
    return await axios.get("product/RecommendedProducts", { params: { productId } });
  },

  categorySearch: async (request: SearchRequest): Promise<SearchResponse> => {
    return await axios.post("search/category", request);
  },

  downloadFiles: async (partNo: string): Promise<Array<Blob>> => {
    return await axios.get("product/downloadfiles", { params: { partNo } });
  },

  getSpecialAccessories: async (partNo: string): Promise<ProductTile[]> => {
    try {
      return await axios.get("product/specialAccessories", {
        params: { partNo },
      });
    } catch (e) {
      return Promise.reject(e);
    }
  },
};

export const CaseApi = {
  searchCases: async (request: SearchCasesRequest): Promise<SearchCasesResponse> => {
    return await axios.post("search/searchcase", request);
  },
};

export const BasketApi = {
  getBasket: async (): Promise<Cart> => {
    const cartStore = useCart();
    try {
      return await axios.get("basket/get", {
        validateStatus(status) {
          if (status === 500) {
            cartStore.isCartModalOpen = true;
          }
          return status >= 200 && status < 300;
        },
      });
    } catch (e) {
      return Promise.reject(e);
    }
  },
  addToBasket: async (partNo: string, quantity: number, incrementExisting: boolean): Promise<Cart> => {
    const cartStore = useCart();
    try {
      emitter.emit("special accessories open", partNo);
      const cart: Cart = await axios.post(
        "basket/addorupdate",
        {
          partNo,
          quantity,
          incrementExisting,
        },
        {
          validateStatus(status) {
            if (status === 500) {
              cartStore.isCartModalOpen = true;
            }

            return status >= 200 && status < 300;
          },
        }
      );

      return cart;
    } catch (e) {
      return Promise.reject(e);
    }
  },
  updateBasket: async (partNo: string, quantity: number): Promise<Cart> => {
    try {
      return await axios.put("basket/update", { partNo, quantity });
    } catch (e) {
      return Promise.reject(e);
    }
  },
  removeFromBasket: async (partNo: string): Promise<Cart> => {
    try {
      return await axios.delete("basket/remove", { params: { partNo } });
    } catch (e) {
      return Promise.reject(e);
    }
  },
  removeRangeFromBasket: async (partNo: string[]): Promise<Cart> => {
    try {
      return await axios.delete("basket/removerange", { params: { partNo } });
    } catch (e) {
      return Promise.reject(e);
    }
  },
  createFromOrder: async (basketId: string): Promise<Cart> => {
    try {
      return await axios.post("basket/createfromorder", null, {
        params: { basketId },
      });
    } catch (e) {
      return Promise.reject(e);
    }
  },
  setDiscountCode: async (discountCode: string): Promise<Cart> => {
    try {
      return await axios.post("basket/discountCode", { discountCode });
    } catch (e) {
      return Promise.reject(e);
    }
  },
};
export const DataLayerApi = {
  createAddToCartEvent: async (partNo: string, quantity: number, itemListName: string) => {
    try {
      const event = await axios.post("dataLayer/createAddToCartEvent", {
        partNo,
        quantity,
        itemListName,
      });
      addToDataLayer(event);
    } catch (e) {
      return Promise.reject(e);
    }
  },
  createRemoveFromCartEvent: async (partNo: string, quantity: number) => {
    try {
      const event = await axios.put("dataLayer/createRemoveFromCartEvent", {
        partNo,
        quantity,
      });
      addToDataLayer(event);
    } catch (e) {
      return Promise.reject(e);
    }
  },
  getUserInfoEvent: async () => {
    try {
      const event = await axios.get("dataLayer/getUserInfoEvent");
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(event);
    } catch (e) {
      return Promise.reject(e);
    }
  },
  getUserLoginEvent: async () => {
    try {
      const event = await axios.get("dataLayer/getUserLoginEvent");
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(event);
    } catch (e) {
      return Promise.reject(e);
    }
  },
  createItemViewEvent: async (partNo: string, quantity: string) => {
    try {
      const event = await axios.post("dataLayer/createItemViewEvent", {
        partNo,
        quantity,
      });
      addToDataLayer(event);
    } catch (e) {
      return Promise.reject(e);
    }
  },
  createViewCartEvent: async () => {
    try {
      const event = await axios.post("dataLayer/createViewCartEvent");
      if (event) {
        addToDataLayer(event);
      }
    } catch (e) {
      return Promise.reject(e);
    }
  },
  createBeginCheckoutEvent: async () => {
    try {
      const event = await axios.post("dataLayer/createBeginCheckoutEvent");
      if (event) {
        addToDataLayer(event);
      }
    } catch (e) {
      return Promise.reject(e);
    }
  },
  updateAddShippingInfoEvent: async () => {
    try {
      const event = await axios.put("dataLayer/updateAddShippingInfoEvent");
      if (event) {
        addToDataLayer(event);
      }
    } catch (e) {
      return Promise.reject(e);
    }
  },
  updateAddPaymentInfoEvent: async (paymentMethodId: string) => {
    try {
      const event = await axios.put("dataLayer/updateAddPaymentInfoEvent", JSON.stringify(paymentMethodId), {
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (event) {
        addToDataLayer(event);
      }
    } catch (e) {
      return Promise.reject(e);
    }
  },
  createSelectItemEvent: async (partNo: string, itemListName: string) => {
    try {
      const event = await axios.post("dataLayer/createSelectItemEvent", {
        partNo,
        itemListName,
        headers: {
          "Content-Type": "application/json",
        },
      });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(event);
    } catch (e) {
      return Promise.reject(e);
    }
  },
  createViewItemListEvent: async (itemListName: string, partNos: string[]) => {
    try {
      const event = await axios.post("dataLayer/createViewItemListEvent", {
        itemListName,
        partNos,
      });
      addToDataLayer(event);
    } catch (e) {
      return Promise.reject(e);
    }
  },
};

export const CheckoutApi = {
  getCheckout: async (): Promise<Checkout> => {
    try {
      return await axios.get("checkout/get");
    } catch (e) {
      return Promise.reject(e);
    }
  },
  getShippingOptions: async (): Promise<FreightModel> => {
    try {
      return await axios.get("checkout/getshippingoptions");
    } catch (e) {
      return Promise.reject(e);
    }
  },
  setPayment: async (paymentMethodId: string): Promise<Checkout> => {
    try {
      return await axios.put("checkout/setpayment", null, {
        params: {
          paymentMethodId,
        },
      });
    } catch (e) {
      return Promise.reject(e);
    }
  },
  setDelivery: async (command: SetDeliveryCommand): Promise<Checkout> => {
    try {
      return await axios.put("checkout/setdelivery", command);
    } catch (e) {
      return Promise.reject(e);
    }
  },
  placeOrder: async (command: PlaceOrderCommand): Promise<string> => {
    try {
      return await axios.post("checkout/placeorder", command);
    } catch (e) {
      return Promise.reject(e);
    }
  },
  updateCustomer: async (command: UpdateCustomerCommand): Promise<void> => {
    try {
      return await axios.post("checkout/updatecustomer", command);
    } catch (e) {
      return Promise.reject(e);
    }
  },
  getPostNordServicePoints: async (zipCode: string): Promise<Array<IServicePoint>> => {
    try {
      return await axios.get("checkout/getpostnordservicepoints", {
        params: { zipCode },
      });
    } catch (e) {
      return Promise.reject(e);
    }
  },
  getDhlServicePoints: async (zipCode: string): Promise<Array<IServicePoint>> => {
    try {
      return await axios.get("checkout/getdhlservicepoints", {
        params: { zipCode },
      });
    } catch (e) {
      return Promise.reject(e);
    }
  },
};

export const MemberApi = {
  login: async (emailAddress: string, password: string): Promise<Login> => {
    try {
      const member = await login(emailAddress, password);
      member.success = true;
      return member;
    } catch (e) {
      return Promise.reject(e);
    }
  },
  logout: async (): Promise<boolean> => {
    try {
      await axios.get("account/signout");
      return true;
    } catch (e) {
      return Promise.reject(e);
    }
  },

  registerAccountPrivate: async (account: RegisterAccountPrivate): Promise<boolean> => {
    try {
      return await axios.post("account/register/private", account);
    } catch (e) {
      return Promise.reject(e);
    }
  },

  registerAccountBusiness: async (account: RegisterAccountBusiness): Promise<boolean> => {
    try {
      return await axios.post("account/register/business", account);
    } catch (e) {
      return Promise.reject(e);
    }
  },

  migrateAccount: async (email: string, name: string, type: string): Promise<boolean> => {
    try {
      return await axios.post("account/migrate", { email, name, type });
    } catch (e) {
      return Promise.reject(e);
    }
  },

  changePassword: async (
    email: string,
    currentPassword: string,
    password: string,
    confirmPassword: string
  ): Promise<ChangePassword> => {
    try {
      const message = await changePassword(email, currentPassword, password, confirmPassword);
      const change: ChangePassword = {
        success: true,
        message: message,
      };
      return change;
    } catch (e) {
      const error = e as Problem;
      throw error;
    }
  },

  validateEmail: async (email: string): Promise<EmailValidatorResult> => {
    try {
      return await axios.post("account/validateemail", { email });
    } catch (e) {
      return Promise.reject(e);
    }
  },

  validateCustomerNumber: async (code: string): Promise<CustomerNumberValidatorResult> => {
    try {
      return await axios.get("account/findcustomerbycode", { params: { code } });
    } catch (e) {
      return Promise.reject(e);
    }
  },
  forgottenPassword: async (email: string): Promise<string> => {
    try {
      return await axios.post("account/forgotpassword", { email });
    } catch (e) {
      return Promise.reject(e);
    }
  },
  resetPassword: async (token: string, email: string, newPassword: string, newPassword2: string): Promise<string> => {
    try {
      return await axios.post("account/resetpassword", {
        email,
        token,
        newPassword,
        newPassword2,
      });
    } catch (e) {
      return Promise.reject(e);
    }
  },
  getOrderHistory: async (): Promise<IOrderHistoryHeader[]> => {
    try {
      return await axios.get("orderhistory/list");
    } catch (e) {
      return Promise.reject(e);
    }
  },
  getOrderHistoryLines: async (id: string, type: OrderHistoryType): Promise<IOrderHistoryLine[]> => {
    try {
      return await axios.get("orderhistory/getlines", { params: { id, type } });
    } catch (e) {
      return Promise.reject(e);
    }
  },
};

export const SalesmanApi = {
  setPostalCookieAndGetSalesman: async (postalCode?: string): Promise<ISalesman> => {
    try {
      return await axios.get("salesman/getandset", {
        params: {
          postalCode: postalCode,
        },
      });
    } catch (e) {
      return Promise.reject(e);
    }
  },
};
export const SearchApi = {
  search: async (query: string, count: number): Promise<QuickSearchViewModel> => {
    try {
      return await axios.post("search/quicksearch", {
        term: query,
        count: count,
      });
    } catch (e) {
      return Promise.reject(e);
    }
  },
  products: async (request: ProductsSearchRequest): Promise<ProductSearchResponse> => {
    try {
      return await axios.post("search/products", request);
    } catch (e) {
      return Promise.reject(e);
    }
  },
};

export const ContentOverviewApi = {
  getByFilter: async (
    id: number,
    subject: number,
    type: number,
    page: number,
    pageSize = 16
  ): Promise<IContentOverviewApiGetByFilterResult> => {
    try {
      return await axios.get("contentoverview/getbyfilter", {
        params: { id, subject, type, page, pageSize },
      });
    } catch (e) {
      return Promise.reject(e);
    }
  },
};

export const UmbracoFormsApi = {
  get: async (id: string): Promise<IUmbracoForm> => {
    try {
      return await umbracoFormsAxios.get(`definitions/${id}`);
    } catch (e) {
      return Promise.reject(e);
    }
  },
  post: async (id: string, values: unknown, antiForgeryToken: IAntiForgeryToken): Promise<void> => {
    try {
      const headers = {};
      headers[antiForgeryToken.headerName] = antiForgeryToken.token;
      return await umbracoFormsAxios.post(
        `entries/${id}`,
        {
          values: values,
        },
        {
          headers: headers,
        }
      );
    } catch (e) {
      return Promise.reject(e);
    }
  },
};
export const UrlApi = {
  get: async (id: string): Promise<string> => {
    try {
      return await axios.get("url/get", {
        params: { id: id },
      });
    } catch (e) {
      return Promise.reject(e);
    }
  },
};

export const FavoritesApi = {
  post: async (partNo: string, value: boolean) => {
    try {
      return await axios.post("favorites/post", {
        partNo: partNo,
        value: value,
      });
    } catch (e) {
      return Promise.reject(e);
    }
  },
  hasFavorites: async (): Promise<boolean> => {
    try {
      return await axios.get("favorites/hasfavorites");
    } catch (e) {
      return Promise.reject(e);
    }
  },
};
